import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { ArrowLeft, ExternalLink, Copy, Calendar, MousePointer, Users, TrendingUp, Globe, Smartphone, Monitor, Tablet, RefreshCw, Download, FileText, FileArchive } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Progress } from '@/components/ui/progress';
import { toast } from 'sonner';
import { apiV1 } from '@/api/client';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

// "Last N days" = N whole South African (UTC+2, no DST) calendar days ending
// now, so the first day's bar is a full day and the buckets line up with the
// SAST days the server groups by. "24h" stays a rolling 24 hours.
const SAST_OFFSET_MS = 2 * 60 * 60 * 1000;
export function sastRangeParams(range, now = new Date()) {
  const end = new Date(now.getTime());
  let start;
  if (range === '24h') {
    start = new Date(end.getTime() - 24 * 60 * 60 * 1000);
  } else {
    const days = { '7d': 7, '30d': 30, '90d': 90 }[range] || 30;
    const sast = new Date(end.getTime() + SAST_OFFSET_MS);
    const midnightUtc = Date.UTC(sast.getUTCFullYear(), sast.getUTCMonth(), sast.getUTCDate() - (days - 1));
    start = new Date(midnightUtc - SAST_OFFSET_MS);
  }
  return `start_date=${start.toISOString()}&end_date=${end.toISOString()}`;
}

export default function URLAnalytics() {
  const { id } = useParams();
  const [urlData, setUrlData] = useState(null);
  const [summary, setSummary] = useState(null);
  const [timeSeries, setTimeSeries] = useState(null);
  const [devices, setDevices] = useState(null);
  const [locations, setLocations] = useState(null);
  const [referrers, setReferrers] = useState(null);
  const [browsers, setBrowsers] = useState(null);
  const [loading, setLoading] = useState(true);
  const [dateRange, setDateRange] = useState('30d');

  useEffect(() => {
    if (id) {
      fetchAllData();
    }
  }, [id, dateRange]);

  const getDateParams = () => sastRangeParams(dateRange);

  const fetchAllData = async () => {
    setLoading(true);
    const dateParams = getDateParams();
    const params = Object.fromEntries(new URLSearchParams(dateParams));

    try {
      const [
        urlRes,
        summaryRes,
        timeSeriesRes,
        devicesRes,
        locationsRes,
        referrersRes,
        browsersRes,
      ] = await Promise.all([
        apiV1.get(`/urls/${id}`),
        apiV1.get(`/urls/${id}/analytics/summary`,     { params }),
        apiV1.get(`/urls/${id}/analytics/time-series`, { params: { ...params, granularity: 'day' } }),
        apiV1.get(`/urls/${id}/analytics/devices`,     { params }),
        apiV1.get(`/urls/${id}/analytics/locations`,   { params }),
        apiV1.get(`/urls/${id}/analytics/referrers`,   { params }),
        apiV1.get(`/urls/${id}/analytics/browsers`,    { params }),
      ]);

      if (urlRes.data?.success)        setUrlData(urlRes.data.data);
      if (summaryRes.data?.success)    setSummary(summaryRes.data.data);
      if (timeSeriesRes.data?.success) setTimeSeries(timeSeriesRes.data.data);
      if (devicesRes.data?.success)    setDevices(devicesRes.data.data);
      if (locationsRes.data?.success)  setLocations(locationsRes.data.data);
      if (referrersRes.data?.success)  setReferrers(referrersRes.data.data);
      if (browsersRes.data?.success)   setBrowsers(browsersRes.data.data);
    } catch (error) {
      console.error('Error fetching analytics:', error);
      const detail =
        error?.message ||
        error?.response?.data?.detail?.message ||
        error?.response?.data?.detail ||
        'Failed to load analytics';
      toast.error(`Failed to load analytics — ${detail}`);
    } finally {
      setLoading(false);
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success('Copied to clipboard!');
  };

  const [downloading, setDownloading] = useState(false);

  const downloadReport = async (format = 'zip') => {
    if (downloading) return;
    setDownloading(true);
    const label = format === 'pdf' ? 'PDF report' : 'data export';
    const toastId = toast.loading(`Preparing ${label}…`);
    try {
      const params = Object.fromEntries(new URLSearchParams(getDateParams()));
      const endpoint =
        format === 'pdf'
          ? `/urls/${id}/analytics/export/pdf`
          : `/urls/${id}/analytics/export`;
      const response = await apiV1.get(endpoint, {
        params,
        responseType: 'blob',
      });

      // Extract filename from Content-Disposition
      const fallbackExt = format === 'pdf' ? 'pdf' : 'zip';
      let filename = `analytics_${urlData?.short_code || id}.${fallbackExt}`;
      const cd = response.headers?.['content-disposition'] || response.headers?.get?.('content-disposition');
      if (cd) {
        const m = /filename="?([^"]+)"?/.exec(cd);
        if (m) filename = m[1];
      }

      const mime = format === 'pdf' ? 'application/pdf' : 'application/zip';
      const blob = new Blob([response.data], { type: mime });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      toast.success(`${label.charAt(0).toUpperCase()}${label.slice(1)} downloaded`, { id: toastId });
    } catch (error) {
      console.error(`Error downloading ${label}:`, error);
      const detail =
        error?.response?.data?.detail?.message ||
        error?.response?.data?.detail ||
        error?.message ||
        `Failed to download ${label}`;
      toast.error(`Download failed — ${detail}`, { id: toastId });
    } finally {
      setDownloading(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading && !urlData) {
    return (
      <div className="space-y-6">
        <div className="flex items-center gap-4">
          <Skeleton className="h-10 w-20" />
          <Skeleton className="h-8 w-64" />
        </div>
        <div className="grid grid-cols-4 gap-4">
          {[...Array(4)].map((_, i) => (
            <Skeleton key={i} className="h-32" />
          ))}
        </div>
        <Skeleton className="h-96" />
      </div>
    );
  }

  if (!urlData) {
    return (
      <div className="text-center py-12">
        <h2 className="text-xl font-semibold mb-2">URL Not Found</h2>
        <p className="text-muted-foreground mb-4">The short URL you're looking for doesn't exist.</p>
        <Link to="/urls">
          <Button>Back to URLs</Button>
        </Link>
      </div>
    );
  }

  const deviceIcons = {
    mobile: Smartphone,
    tablet: Tablet,
    desktop: Monitor,
    other: Monitor,
  };

  return (
    <div className="space-y-6" data-testid="url-analytics-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div className="flex items-center gap-4">
          <Link to="/urls">
            <Button variant="ghost" size="icon">
              <ArrowLeft className="w-4 h-4" />
            </Button>
          </Link>
          <div>
            <h1 className="text-2xl font-semibold">{urlData.title}</h1>
            <div className="flex items-center gap-2 text-muted-foreground">
              <code className="text-sm bg-muted px-2 py-0.5 rounded">
                {urlData.short_url?.replace('https://', '')}
              </code>
              <Button variant="ghost" size="icon" className="h-6 w-6" onClick={() => copyToClipboard(urlData.short_url)}>
                <Copy className="w-3 h-3" />
              </Button>
              <a href={urlData.short_url} target="_blank" rel="noopener noreferrer">
                <Button variant="ghost" size="icon" className="h-6 w-6">
                  <ExternalLink className="w-3 h-3" />
                </Button>
              </a>
            </div>
          </div>
        </div>
        
        <div className="flex items-center gap-2">
          <Select value={dateRange} onValueChange={setDateRange}>
            <SelectTrigger className="w-[150px]">
              <Calendar className="w-4 h-4 mr-2" />
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="24h">Last 24 hours</SelectItem>
              <SelectItem value="7d">Last 7 days</SelectItem>
              <SelectItem value="30d">Last 30 days</SelectItem>
              <SelectItem value="90d">Last 90 days</SelectItem>
            </SelectContent>
          </Select>
          <Button variant="outline" size="icon" onClick={fetchAllData} disabled={loading} data-testid="refresh-analytics-button" aria-label="Refresh">
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          </Button>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant="outline"
                disabled={downloading || loading}
                data-testid="download-analytics-report-button"
                className="gap-2"
              >
                <Download className={`w-4 h-4 ${downloading ? 'animate-pulse' : ''}`} />
                <span className="hidden sm:inline">{downloading ? 'Preparing…' : 'Download'}</span>
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-56">
              <DropdownMenuLabel>Download report</DropdownMenuLabel>
              <DropdownMenuSeparator />
              <DropdownMenuItem
                onClick={() => downloadReport('pdf')}
                disabled={downloading}
                data-testid="download-analytics-pdf"
              >
                <FileText className="w-4 h-4 mr-2" />
                <div className="flex flex-col">
                  <span>Branded PDF</span>
                  <span className="text-xs text-muted-foreground">For sharing & printing</span>
                </div>
              </DropdownMenuItem>
              <DropdownMenuItem
                onClick={() => downloadReport('zip')}
                disabled={downloading}
                data-testid="download-analytics-zip"
              >
                <FileArchive className="w-4 h-4 mr-2" />
                <div className="flex flex-col">
                  <span>Data export (.zip)</span>
                  <span className="text-xs text-muted-foreground">CSVs incl. raw clicks</span>
                </div>
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardHeader className="pb-2">
            <CardDescription className="flex items-center gap-2">
              <MousePointer className="w-4 h-4" />
              Total Clicks
            </CardDescription>
            <CardTitle className="text-3xl">{summary?.total_clicks?.toLocaleString() || 0}</CardTitle>
          </CardHeader>
          <CardContent>
            {summary?.bot_clicks > 0 ? (
              <p className="text-xs text-muted-foreground" data-testid="bot-clicks-note">
                {summary.bot_clicks.toLocaleString()} automated {summary.bot_clicks === 1 ? 'request' : 'requests'} (link previews, crawlers) not counted
              </p>
            ) : (
              <p className="text-xs text-muted-foreground">People only - automated requests are not counted</p>
            )}
          </CardContent>
        </Card>
        
        <Card>
          <CardHeader className="pb-2">
            <CardDescription className="flex items-center gap-2">
              <Users className="w-4 h-4" />
              Unique Clicks
            </CardDescription>
            <CardTitle className="text-3xl">{summary?.unique_clicks?.toLocaleString() || 0}</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-xs text-muted-foreground">Distinct visitors in this period</p>
          </CardContent>
        </Card>
        
        <Card>
          <CardHeader className="pb-2">
            <CardDescription className="flex items-center gap-2">
              <TrendingUp className="w-4 h-4" />
              Unique Rate
            </CardDescription>
            <CardTitle className="text-3xl">{summary?.unique_rate || 0}%</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-xs text-muted-foreground">Unique / Total clicks</p>
          </CardContent>
        </Card>
        
        <Card>
          <CardHeader className="pb-2">
            <CardDescription className="flex items-center gap-2">
              <Calendar className="w-4 h-4" />
              Last Click
            </CardDescription>
            <CardTitle className="text-lg">
              {urlData.last_click_at ? formatDate(urlData.last_click_at) : 'Never'}
            </CardTitle>
          </CardHeader>
        </Card>
      </div>

      {/* URL Details */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <Card className="md:col-span-2">
          <CardHeader>
            <CardTitle>URL Details</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <div className="text-sm text-muted-foreground">Destination URL</div>
              <div className="font-mono text-sm break-all">{urlData.destination_url}</div>
            </div>
            
            {urlData.tracked_url !== urlData.destination_url && (
              <div>
                <div className="text-sm text-muted-foreground">Tracked URL (with UTM)</div>
                <div className="font-mono text-sm break-all">{urlData.tracked_url}</div>
              </div>
            )}
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <div className="text-sm text-muted-foreground">Created</div>
                <div>{formatDate(urlData.created_at)}</div>
              </div>
              <div>
                <div className="text-sm text-muted-foreground">Status</div>
                <Badge variant={urlData.status === 'active' ? 'default' : 'secondary'} className="capitalize">
                  {urlData.status}
                </Badge>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Tracking Parameters</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {urlData.tracking?.region_name && (
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Region</span>
                  <Badge variant="outline">{urlData.tracking.region_name}</Badge>
                </div>
              )}
              {urlData.tracking?.branch_name && (
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Branch</span>
                  <Badge variant="outline">{urlData.tracking.branch_name}</Badge>
                </div>
              )}
              {urlData.tracking?.agent_name && (
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Agent</span>
                  <Badge variant="outline">{urlData.tracking.agent_name}</Badge>
                </div>
              )}
              {urlData.tracking?.listing_ref && (
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Listing</span>
                  <Badge variant="secondary">{urlData.tracking.listing_ref}</Badge>
                </div>
              )}
              {!urlData.tracking?.region_name && !urlData.tracking?.branch_name && 
               !urlData.tracking?.agent_name && !urlData.tracking?.listing_ref && (
                <p className="text-sm text-muted-foreground text-center py-4">
                  No tracking parameters set
                </p>
              )}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Analytics Tabs */}
      <Tabs defaultValue="overview" className="space-y-4">
        <TabsList>
          <TabsTrigger value="overview">Overview</TabsTrigger>
          <TabsTrigger value="devices">Devices</TabsTrigger>
          <TabsTrigger value="locations">Locations</TabsTrigger>
          <TabsTrigger value="referrers">Referrers</TabsTrigger>
        </TabsList>
        
        <TabsContent value="overview">
          <Card>
            <CardHeader>
              <CardTitle>Click Activity</CardTitle>
              <CardDescription>Daily click trends</CardDescription>
            </CardHeader>
            <CardContent>
              {timeSeries?.data_points?.length > 0 ? (
                <div className="space-y-4">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Date</TableHead>
                        <TableHead className="text-right">Total Clicks</TableHead>
                        <TableHead className="text-right">Unique Clicks</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {timeSeries.data_points.slice(-10).map((point, idx) => (
                        <TableRow key={idx}>
                          <TableCell>{point.period}</TableCell>
                          <TableCell className="text-right font-medium">{point.total_clicks}</TableCell>
                          <TableCell className="text-right">{point.unique_clicks}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              ) : (
                <div className="h-64 flex items-center justify-center text-muted-foreground">
                  <div className="text-center">
                    <TrendingUp className="w-12 h-12 mx-auto mb-2 opacity-50" />
                    <p>No click data yet</p>
                    <p className="text-sm">Share your link to start collecting analytics</p>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
        
        <TabsContent value="devices">
          <Card>
            <CardHeader>
              <CardTitle>Device Breakdown</CardTitle>
              <CardDescription>Clicks by device type</CardDescription>
            </CardHeader>
            <CardContent>
              {devices?.breakdown?.length > 0 ? (
                <div className="space-y-4">
                  {devices.breakdown.map((device, idx) => {
                    const Icon = deviceIcons[device.type] || Monitor;
                    return (
                      <div key={idx} className="space-y-2">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-2">
                            <Icon className="w-4 h-4 text-muted-foreground" />
                            <span className="capitalize">{device.type}</span>
                          </div>
                          <span className="text-sm">
                            {device.count} ({device.percentage}%)
                          </span>
                        </div>
                        <Progress value={device.percentage} className="h-2" />
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div className="grid grid-cols-3 gap-4">
                  <div className="text-center p-4 bg-muted/50 rounded-lg">
                    <Monitor className="w-8 h-8 mx-auto mb-2 text-primary" />
                    <div className="text-2xl font-semibold">-</div>
                    <div className="text-sm text-muted-foreground">Desktop</div>
                  </div>
                  <div className="text-center p-4 bg-muted/50 rounded-lg">
                    <Smartphone className="w-8 h-8 mx-auto mb-2 text-primary" />
                    <div className="text-2xl font-semibold">-</div>
                    <div className="text-sm text-muted-foreground">Mobile</div>
                  </div>
                  <div className="text-center p-4 bg-muted/50 rounded-lg">
                    <Tablet className="w-8 h-8 mx-auto mb-2 text-primary" />
                    <div className="text-2xl font-semibold">-</div>
                    <div className="text-sm text-muted-foreground">Tablet</div>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Browser breakdown */}
          <Card className="mt-4">
            <CardHeader>
              <CardTitle>Browser Breakdown</CardTitle>
            </CardHeader>
            <CardContent>
              {browsers?.browsers?.length > 0 ? (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Browser</TableHead>
                      <TableHead className="text-right">Clicks</TableHead>
                      <TableHead className="text-right">Share</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {browsers.browsers.map((browser, idx) => (
                      <TableRow key={idx}>
                        <TableCell>{browser.browser}</TableCell>
                        <TableCell className="text-right">{browser.count}</TableCell>
                        <TableCell className="text-right">{browser.percentage}%</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              ) : (
                <p className="text-center text-muted-foreground py-4">No data yet</p>
              )}
            </CardContent>
          </Card>
        </TabsContent>
        
        <TabsContent value="locations">
          <Card>
            <CardHeader>
              <CardTitle>Geographic Distribution</CardTitle>
              <CardDescription>Clicks by country</CardDescription>
            </CardHeader>
            <CardContent>
              {locations?.countries?.length > 0 ? (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Country</TableHead>
                      <TableHead className="text-right">Clicks</TableHead>
                      <TableHead className="text-right">Share</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {locations.countries.map((country, idx) => (
                      <TableRow key={idx}>
                        <TableCell>
                          {country.name || country.code}
                        </TableCell>
                        <TableCell className="text-right">{country.count}</TableCell>
                        <TableCell className="text-right">{country.percentage}%</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              ) : (
                <div className="h-64 flex items-center justify-center text-muted-foreground">
                  <div className="text-center">
                    <Globe className="w-12 h-12 mx-auto mb-2 opacity-50" />
                    <p>No location data yet</p>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
        
        <TabsContent value="referrers">
          <Card>
            <CardHeader>
              <CardTitle>Top Referrers</CardTitle>
              <CardDescription>Where clicks are coming from</CardDescription>
            </CardHeader>
            <CardContent>
              {referrers?.referrers?.length > 0 ? (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Referrer</TableHead>
                      <TableHead className="text-right">Clicks</TableHead>
                      <TableHead className="text-right">Share</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {referrers.referrers.map((ref, idx) => (
                      <TableRow key={idx}>
                        <TableCell className="font-mono text-sm truncate max-w-[300px]">
                          {ref.referrer || 'Direct'}
                        </TableCell>
                        <TableCell className="text-right">{ref.count}</TableCell>
                        <TableCell className="text-right">{ref.percentage}%</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              ) : (
                <div className="h-64 flex items-center justify-center text-muted-foreground">
                  <div className="text-center">
                    <ExternalLink className="w-12 h-12 mx-auto mb-2 opacity-50" />
                    <p>No referrer data yet</p>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
